const policy = require('../config/matchPermissionPolicy.json');

const STATUS_SET = new Set(Array.isArray(policy.statuses) ? policy.statuses : []);
const ROLE_SET = new Set(Array.isArray(policy.roles) ? policy.roles : []);
const CAPABILITY_LIST = Array.isArray(policy.capabilities) ? policy.capabilities : [];
const CAPABILITY_SET = new Set(CAPABILITY_LIST);
const ROLE_PRIORITY = Array.isArray(policy.rolePriority) ? policy.rolePriority : [];
const ROLE_PRIORITY_INDEX = new Map(ROLE_PRIORITY.map((role, idx) => [role, idx]));
const GLOBAL_ROLE_OVERRIDES = new Set(Array.isArray(policy.globalRoleOverrides) ? policy.globalRoleOverrides : []);

function normalizeRole(rawRole) {
  const role = String(rawRole || '').trim();
  if (!role) return '';
  const alias = policy.roleAliases && policy.roleAliases[role];
  const normalized = String(alias || role).trim();
  return normalized;
}

function normalizeStatus(rawStatus) {
  const status = String(rawStatus || '').trim();
  if (STATUS_SET.has(status)) return status;
  return '可报名';
}

function isKnownRole(role) {
  return ROLE_SET.has(normalizeRole(role));
}

function shouldUseGlobalRole(globalRole) {
  const role = normalizeRole(globalRole);
  return !!role && GLOBAL_ROLE_OVERRIDES.has(role);
}

function resolveRoleByPriority(roles) {
  const list = (Array.isArray(roles) ? roles : [])
    .map((role) => normalizeRole(role))
    .filter((role) => ROLE_SET.has(role));
  if (!list.length) return null;
  list.sort((a, b) => {
    const ai = ROLE_PRIORITY_INDEX.has(a) ? ROLE_PRIORITY_INDEX.get(a) : 10_000;
    const bi = ROLE_PRIORITY_INDEX.has(b) ? ROLE_PRIORITY_INDEX.get(b) : 10_000;
    return ai - bi;
  });
  return list[0] || null;
}

function resolveEffectiveRole({ globalRole, matchRoles }) {
  const normalizedGlobal = normalizeRole(globalRole);
  if (shouldUseGlobalRole(normalizedGlobal)) {
    return { resolvedRole: normalizedGlobal, source: 'global' };
  }
  const resolvedMatchRole = resolveRoleByPriority(matchRoles || []);
  if (resolvedMatchRole) {
    return { resolvedRole: resolvedMatchRole, source: 'match' };
  }
  if (ROLE_SET.has(normalizedGlobal)) {
    return { resolvedRole: normalizedGlobal, source: 'global' };
  }
  return { resolvedRole: 'user', source: 'default' };
}

function getCapabilitiesForRoleStatus(rawRole, rawStatus) {
  const role = normalizeRole(rawRole) || 'user';
  const status = normalizeStatus(rawStatus);
  const matrix = policy.matrix || {};
  const rolePolicy = matrix[role] || {};

  const caps = new Set();

  const addFromList = (items) => {
    if (!Array.isArray(items)) return;
    for (const item of items) {
      const cap = String(item || '').trim();
      if (!cap) continue;
      if (cap === '*') {
        for (const c of CAPABILITY_LIST) caps.add(c);
        continue;
      }
      if (CAPABILITY_SET.has(cap)) caps.add(cap);
    }
  };

  addFromList(rolePolicy['*']);
  addFromList(rolePolicy[status]);

  const allRoleStatusCaps = policy.statusCapabilitiesForAllRoles || {};
  addFromList(allRoleStatusCaps[status]);

  // 管理员默认拥有所有能力（防止配置遗漏）
  if (role === 'admin') {
    for (const c of CAPABILITY_LIST) caps.add(c);
  }

  return caps;
}

function toCapabilityMap(capabilitySet) {
  const set = capabilitySet instanceof Set ? capabilitySet : new Set(Array.isArray(capabilitySet) ? capabilitySet : []);
  const out = {};
  for (const cap of CAPABILITY_LIST) {
    out[cap] = set.has(cap);
  }
  return out;
}

function hasCapabilities(capabilitySet, requiredCapabilities, requireAll = false) {
  const set = capabilitySet instanceof Set ? capabilitySet : new Set(Array.isArray(capabilitySet) ? capabilitySet : []);
  const required = (Array.isArray(requiredCapabilities) ? requiredCapabilities : [])
    .map((item) => String(item || '').trim())
    .filter((item) => CAPABILITY_SET.has(item));
  if (!required.length) return false;
  if (requireAll) {
    return required.every((cap) => set.has(cap));
  }
  return required.some((cap) => set.has(cap));
}

function getRouteRule(routeKey) {
  const routes = policy.routes || {};
  const rule = routes[String(routeKey || '').trim()];
  if (!rule) return null;
  const anyOf = (Array.isArray(rule.anyOf) ? rule.anyOf : [])
    .map((item) => String(item || '').trim())
    .filter((item) => CAPABILITY_SET.has(item));
  const allOf = (Array.isArray(rule.allOf) ? rule.allOf : [])
    .map((item) => String(item || '').trim())
    .filter((item) => CAPABILITY_SET.has(item));
  if (!anyOf.length && !allOf.length) return null;
  return { anyOf, allOf };
}

function matchRouteRule(capabilitySet, routeRule) {
  if (!routeRule) return false;
  const set = capabilitySet instanceof Set ? capabilitySet : new Set(Array.isArray(capabilitySet) ? capabilitySet : []);
  if (Array.isArray(routeRule.allOf) && routeRule.allOf.length > 0) {
    if (!routeRule.allOf.every((cap) => set.has(cap))) return false;
  }
  if (Array.isArray(routeRule.anyOf) && routeRule.anyOf.length > 0) {
    if (!routeRule.anyOf.some((cap) => set.has(cap))) return false;
  }
  return true;
}

module.exports = {
  policy,
  normalizeRole,
  normalizeStatus,
  isKnownRole,
  shouldUseGlobalRole,
  resolveRoleByPriority,
  resolveEffectiveRole,
  getCapabilitiesForRoleStatus,
  toCapabilityMap,
  hasCapabilities,
  getRouteRule,
  matchRouteRule,
};
